import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useOptionalLifeOS } from './LifeOSProvider';
import {
  lifeOSClassNames,
  lifeOSItemIsPast,
  lifeOSItemTitle,
  lifeOSRefreshTime,
  lifeOSStatusLabel,
  lifeOSTime,
  lifeOSTimeline,
  lifeOSTypeLabel,
} from './presentation';
import './life-os.css';

const SUMMARY_LABELS = {
  events: 'compromissos',
  tasks: 'tarefas',
  reminders: 'lembretes',
  routines: 'rotinas',
  project_deadlines: 'prazos',
  open_loops: 'pendências',
  completed: 'concluídos',
};

function timelineTime(item) {
  return item?.time || item?.start_at || item?.due_at || item?.start_time || '';
}

function todayItemTitle(item) {
  return lifeOSItemTitle(item) || lifeOSTypeLabel(item?.type) || 'Item do dia';
}

function uniqueTimelineItems(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = item?.id || item?.entity_id;
    if (!key || seen.has(key)) return !key;
    seen.add(key);
    return true;
  });
}

function TimelineRow({ item, date, timezone, onSelectItem }) {
  const past = lifeOSItemIsPast(item, date, timezone);
  const time = lifeOSTime(timelineTime(item), timezone);
  const endTime = lifeOSTime(item?.end_time, timezone);
  const status = lifeOSStatusLabel(item?.status);
  const type = lifeOSTypeLabel(item?.type);
  const title = todayItemTitle(item);
  const content = (
    <>
      <div className="life-os-timeline__time" aria-label={time ? `Horário ${time}` : 'Sem horário'}>
        {time || '—'}
      </div>
      <div className="life-os-timeline__rail" aria-hidden="true"><span /></div>
      <div className="life-os-timeline__body">
        <div className="life-os-timeline__title-row">
          <span className="life-os-timeline__title">{title}</span>
          {type && <span className="life-os-kind">{type}</span>}
        </div>
        {(endTime || status || item?.source) && (
          <div className="life-os-timeline__meta">
            {endTime && <span>até {endTime}</span>}
            {status && <span>{status}</span>}
            {item?.source && <span>{String(item.source)}</span>}
          </div>
        )}
      </div>
    </>
  );

  if (onSelectItem) {
    return (
      <li className={lifeOSClassNames('life-os-timeline__item', past && 'is-past')}>
        <button
          type="button"
          className="life-os-timeline__action"
          onClick={() => onSelectItem(item)}
          aria-label={`Abrir ${title}`}
        >
          {content}
        </button>
      </li>
    );
  }
  return <li className={lifeOSClassNames('life-os-timeline__item', past && 'is-past')}>{content}</li>;
}

function LoadingTimeline() {
  return (
    <div className="life-os-loading-list" aria-label="Carregando linha do tempo" role="status">
      {[0, 1, 2].map(index => (
        <div className="life-os-skeleton life-os-skeleton--timeline" key={index} aria-hidden="true" />
      ))}
      <span className="life-os-sr-only">Carregando linha do tempo…</span>
    </div>
  );
}

/**
 * A bounded, vertical day view. It can read directly from LifeOSProvider or
 * be controlled with `data`/`state` for an embedded or test surface.
 */
export function TodayPanel({
  data,
  state,
  date,
  timezone,
  autoLoad = true,
  className,
  title = 'Hoje',
  maxVisibleItems = 12,
  showSummary = true,
  emptyMessage = 'Não há itens planejados para este dia.',
  onRefresh,
  onSelectItem,
  onOpenRoutines,
  onCreateRoutine,
}) {
  const panelId = useId();
  const lifeOS = useOptionalLifeOS();
  const providerState = lifeOS?.today;
  const effectiveState = state || providerState || {};
  const response = data !== undefined ? data : effectiveState.data;
  const loading = data !== undefined ? false : Boolean(effectiveState.loading);
  const error = data !== undefined ? '' : effectiveState.error;
  const selectedDate = date || effectiveState.date || response?.date || null;
  const selectedTimezone = timezone || effectiveState.timezone || response?.timezone || undefined;
  const refresh = onRefresh || (() => lifeOS?.loadToday({ date, timezone }));
  const [expanded, setExpanded] = useState(false);
  const attemptedAutoLoadRef = useRef('');
  const openedTodayRef = useRef('');

  useEffect(() => {
    if (!autoLoad || data !== undefined || state?.data || !lifeOS || loading) return;
    const hasSelectedDay = Boolean(lifeOS.today?.data)
      && (!date || lifeOS.today?.date === date)
      && (!timezone || lifeOS.today?.timezone === timezone);
    const key = `${date || 'current'}:${timezone || 'default'}`;
    if (!hasSelectedDay && attemptedAutoLoadRef.current !== key) {
      attemptedAutoLoadRef.current = key;
      void lifeOS.loadToday({ date, timezone });
    }
  }, [autoLoad, data, date, lifeOS, loading, state?.data, timezone]);

  useEffect(() => {
    if (!response?.date || effectiveState.stale || !lifeOS?.trackTechnicalEvent) return;
    const key = `${response.date}:${response.timezone || selectedTimezone || ''}`;
    if (openedTodayRef.current === key) return;
    openedTodayRef.current = key;
    void lifeOS.trackTechnicalEvent('today_open');
  }, [effectiveState.stale, lifeOS, response?.date, response?.timezone, selectedTimezone]);

  const timeline = useMemo(() => lifeOSTimeline(response), [response]);
  const scheduled = useMemo(() => timeline.filter(item => timelineTime(item)), [timeline]);
  const unscheduled = useMemo(() => {
    const toPlace = Array.isArray(response?.to_place)
      ? response.to_place
      : [...(Array.isArray(response?.unscheduled_tasks) ? response.unscheduled_tasks : []), ...(Array.isArray(response?.open_loops) ? response.open_loops : [])];
    return uniqueTimelineItems([...timeline.filter(item => !timelineTime(item)), ...toPlace]);
  }, [response, timeline]);
  const focusItem = useMemo(() => (
    response?.current_item || response?.next_item || scheduled.find(item => !lifeOSItemIsPast(item, selectedDate, selectedTimezone)) || null
  ), [response, scheduled, selectedDate, selectedTimezone]);
  const maximum = Math.max(1, Number(maxVisibleItems) || 12);
  const visibleScheduled = expanded ? scheduled : scheduled.slice(0, maximum);
  const stale = Boolean(effectiveState.stale || (!lifeOS?.online && response));
  const refreshTime = lifeOSRefreshTime(effectiveState.fetchedAt, selectedTimezone);
  const summaryItems = Object.entries(response?.summary || {})
    .filter(([key, value]) => SUMMARY_LABELS[key] && Number.isFinite(Number(value)))
    .map(([key, value]) => ({ key, value: Number(value), label: SUMMARY_LABELS[key] }));
  const freeBlocks = Array.isArray(response?.free_blocks) ? response.free_blocks : [];

  return (
    <section className={lifeOSClassNames('life-os-panel', 'life-os-today', className)} aria-labelledby={panelId}>
      <header className="life-os-panel__header">
        <div>
          <p className="life-os-eyebrow">ATHENA TODAY</p>
          <h2 id={panelId}>{title}</h2>
          {selectedDate && <p className="life-os-panel__subheading">{selectedDate}</p>}
        </div>
        <div className="life-os-panel__tools">
          {lifeOS?.context && (
            <label title="Altera apenas a relevância visual; seus dados continuam juntos." style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'rgba(214,235,250,.68)', fontSize: 11 }}>
              <span className="life-os-sr-only">Contexto de relevância</span>
              <select
                aria-label="Contexto de relevância"
                value={lifeOS.context.preference}
                onChange={event => lifeOS.context.setPreference(event.target.value)}
                style={{ minHeight: 36, borderRadius: 9, border: '1px solid rgba(122,205,255,.22)', background: 'rgba(8,24,53,.72)', color: 'rgba(224,245,255,.88)', padding: '0 7px', fontSize: 11 }}
              >
                <option value="auto">Auto · {lifeOS.context.effective === 'work' ? 'Trabalho' : 'Pessoal'}</option>
                <option value="work">Trabalho</option>
                <option value="personal">Pessoal</option>
              </select>
            </label>
          )}
          {(onOpenRoutines || onCreateRoutine) && (
            <button
              type="button"
              className="life-os-button life-os-button--quiet"
              onClick={onOpenRoutines || onCreateRoutine}
              aria-label={onOpenRoutines ? 'Abrir rotinas' : 'Criar rotina'}
            >
              Rotinas
            </button>
          )}
          <button
            type="button"
            className="life-os-icon-button"
            onClick={() => { void refresh?.(); }}
            disabled={loading}
            aria-label="Atualizar linha do tempo de hoje"
            title="Atualizar"
          >
            ↻
          </button>
        </div>
      </header>

      {(stale || error) && (
        <div className={lifeOSClassNames('life-os-notice', error && !response && 'life-os-notice--error')} role={error ? 'alert' : 'status'}>
          <span>{error || `Dados salvos${refreshTime ? ` · atualizados às ${refreshTime}` : ''}.`}</span>
          {error && (
            <button type="button" className="life-os-link-button" onClick={() => { void refresh?.(); }} disabled={loading}>
              Tentar novamente
            </button>
          )}
        </div>
      )}

      {response && focusItem && (
        <section className="life-os-focus" aria-label={response?.current_item ? 'Foco atual' : 'Próximo foco'}>
          <p className="life-os-eyebrow">{response?.current_item ? 'AGORA' : 'PRÓXIMO'}</p>
          <div className="life-os-focus__row">
            <div>
              <h3>{todayItemTitle(focusItem)}</h3>
              <p>
                {[lifeOSTime(timelineTime(focusItem), selectedTimezone), lifeOSTypeLabel(focusItem?.type)]
                  .filter(Boolean).join(' · ')}
              </p>
            </div>
            {onSelectItem && (
              <button type="button" className="life-os-button" onClick={() => onSelectItem(focusItem)}>
                Abrir
              </button>
            )}
          </div>
        </section>
      )}

      {showSummary && response && summaryItems.length > 0 && (
        <div className="life-os-summary" aria-label="Resumo do dia">
          {summaryItems.map(item => (
            <span key={item.key}><strong>{item.value}</strong> {item.label}</span>
          ))}
        </div>
      )}

      {loading && !response ? <LoadingTimeline /> : (
        <>
          {scheduled.length > 0 && (
            <ol className="life-os-timeline" aria-label="Linha do tempo do dia">
              {visibleScheduled.map((item, index) => (
                <TimelineRow
                  item={item}
                  key={item?.id || `${item?.entity_id || item?.title || 'timeline'}-${index}`}
                  date={selectedDate}
                  timezone={selectedTimezone}
                  onSelectItem={onSelectItem}
                />
              ))}
            </ol>
          )}

          {scheduled.length > maximum && (
            <button type="button" className="life-os-button life-os-button--quiet life-os-show-more" onClick={() => setExpanded(value => !value)}>
              {expanded ? 'Mostrar menos' : `Mostrar mais ${scheduled.length - maximum} itens`}
            </button>
          )}

          {unscheduled.length > 0 && (
            <section className="life-os-to-place" aria-labelledby={`${panelId}-to-place`}>
              <h3 id={`${panelId}-to-place`}>Para encaixar</h3>
              <ul>
                {unscheduled.slice(0, maximum).map((item, index) => (
                  <li key={item?.id || `${item?.entity_id || item?.title || 'unscheduled'}-${index}`}>
                    {onSelectItem ? (
                      <button type="button" onClick={() => onSelectItem(item)} aria-label={`Abrir ${todayItemTitle(item)}`}>
                        <span>{todayItemTitle(item)}</span>
                        {lifeOSTypeLabel(item?.type) && <small>{lifeOSTypeLabel(item.type)}</small>}
                      </button>
                    ) : (
                      <><span>{todayItemTitle(item)}</span>{lifeOSTypeLabel(item?.type) && <small>{lifeOSTypeLabel(item.type)}</small>}</>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {freeBlocks.length > 0 && (
            <section className="life-os-free-blocks" aria-labelledby={`${panelId}-free-blocks`}>
              <h3 id={`${panelId}-free-blocks`}>Tempo livre</h3>
              <div>
                {freeBlocks.slice(0, 4).map((block, index) => {
                  const start = lifeOSTime(block?.start_at || block?.start, selectedTimezone);
                  const end = lifeOSTime(block?.end_at || block?.end, selectedTimezone);
                  return (
                    <span key={`${start}-${end}-${index}`}>
                      {start && end ? `${start}–${end}` : block?.duration_minutes ? `${block.duration_minutes} min` : 'Disponível'}
                    </span>
                  );
                })}
              </div>
            </section>
          )}

          {!loading && !error && timeline.length === 0 && unscheduled.length === 0 && (
            <div className="life-os-empty" role="status">
              <p>{emptyMessage}</p>
              {onCreateRoutine && <button type="button" className="life-os-button" onClick={onCreateRoutine}>Criar rotina</button>}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default TodayPanel;
